// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { QuickTree } from './QuickTree';
import { QuickTreeNode } from './QuickTreeNode';
import { ThemeManager } from '../../misc/ThemeManager';
import { Rectangle } from '@shared/commons/Geometry';
import { ShadowBrush, SolidBrush } from '@shared/commons/Graphics';
import { Resources } from '@shared/localizations/Resources';
import { SymbolLookupViewItemHelper } from '@shared/utils/Instruments/SymbolLookupViewItemHelper';
import { QuickTableUtils } from '../QuickTable/QuickTableUtils';
export class InstrumentLookupQuickTreeNode extends QuickTreeNode {
    constructor(text) {
        super(text);
        this.InstrumentNameText = '';
        this.InstrumentTradingExchangeText = '';
        this.InstrumentDescriptionText = '';
        this.FlagImg = null;
        this.SpecificTag = '';
        this.FullContainsCount = 0;
        this.SkipRequest = false;
        this.sortingStop = false;
        this.instrumentTypeText = '';
        this.instrumentTypeColor = '';
    }
    Draw(ctx, backgroundColor, curY, rowHeight, qtWidth, isHoveredNode, nodeOffset) {
        const font = InstrumentLookupQuickTreeNode.FontObj;
        ctx.font = InstrumentLookupQuickTreeNode.Font;
        const rightMargin = 16;
        const topPadding = 8;
        let curX = QuickTree.COLLAPSE_ICON_SIZE * 2;
        if (this.nodeLevel > 1) {
            nodeOffset = isValidNumber(nodeOffset) ? nodeOffset : InstrumentLookupQuickTreeNode.InstrumentIconWidth + 4;
            curX += (this.nodeLevel - 1) * nodeOffset;
        }
        const hasDescription = isValidString(this.InstrumentDescriptionText);
        let withCollapse = true;
        if (this.HasChildNodes()) {
            curX -= QuickTree.COLLAPSE_ICON_SIZE;
            this.CollapseRectangle.X = curX;
            this.CollapseRectangle.Y = hasDescription
                ? curY + topPadding
                : curY + rowHeight / 2 - QuickTree.COLLAPSE_ICON_SIZE / 2;
            this.CollapseRectangle.Width = QuickTree.COLLAPSE_ICON_SIZE;
            this.CollapseRectangle.Height = QuickTree.COLLAPSE_ICON_SIZE;
            curX += this.CollapseRectangle.Width;
        }
        else {
            this.CollapseRectangle.Empty();
            withCollapse = false;
        }
        if (isValidString(this.instrumentTypeText)) {
            curX += 8; // gap
            this.ImgRectangle.X = curX;
            this.ImgRectangle.Y = hasDescription
                ? curY + topPadding
                : curY + rowHeight / 2 - InstrumentLookupQuickTreeNode.InstrumentIconHeight / 2;
            this.ImgRectangle.Width = InstrumentLookupQuickTreeNode.InstrumentIconWidth;
            this.ImgRectangle.Height = InstrumentLookupQuickTreeNode.InstrumentIconHeight;
            curX += this.ImgRectangle.Width;
        }
        else {
            this.ImgRectangle.Empty();
        }
        let exchangeTextRect = null;
        const gapBetweenExAndFlag = 4;
        if (isValidString(this.InstrumentTradingExchangeText)) {
            const tradingExchangeTextWidth = Math.ceil(QuickTableUtils.GetWidth(ctx, this.InstrumentTradingExchangeText));
            const exchangeXOffset = qtWidth -
                InstrumentLookupQuickTreeNode.InsFlagWidth -
                tradingExchangeTextWidth -
                rightMargin -
                gapBetweenExAndFlag;
            const exchangeYOffset = hasDescription
                ? curY + topPadding
                : curY + rowHeight / 2 - font.getCorrectHeight() / 2;
            exchangeTextRect = new Rectangle(exchangeXOffset, exchangeYOffset, tradingExchangeTextWidth, font.getCorrectHeight());
        }
        curX += 4; // gap
        this.TextRectangle.X = curX;
        this.TextRectangle.Y = hasDescription
            ? curY + topPadding
            : curY + rowHeight / 2 - font.getCorrectHeight() / 2 + 2; // 2 - offset to align text with icon
        this.TextRectangle.Width = !isNullOrUndefined(exchangeTextRect)
            ? exchangeTextRect.X - curX - 4
            : qtWidth - curX - rightMargin; // 4 - gap
        this.TextRectangle.Height = font.getCorrectHeight();
        this.DisplayRectangle = new Rectangle(0, curY, qtWidth, rowHeight);
        let backgroundBrush = null;
        if (this.selected) {
            backgroundBrush = new SolidBrush(ThemeManager.colors.quickTreeNodeBackgroundSelected);
        }
        else if (isHoveredNode) {
            backgroundBrush = new SolidBrush(ThemeManager.colors.quickTreeNodeBackgroundHovered);
        }
        else {
            backgroundBrush = new SolidBrush(backgroundColor);
        }
        ctx.FillRectWithRect(backgroundBrush, this.DisplayRectangle);
        InstrumentLookupQuickTreeNode.DrawInstrumentTypeLogo(ctx, this.ImgRectangle, this.instrumentTypeText, this.instrumentTypeColor);
        if (withCollapse) {
            this.drawCollapseIcon(ctx, isHoveredNode);
        }
        const expanded = withCollapse && !this.collapsed;
        const textBrush = new SolidBrush(this.getForegroundColor(expanded, isHoveredNode));
        ctx.textBaseline = 'top';
        ctx.DrawStringInRectNew(this.text, textBrush, this.TextRectangle, true);
        ctx.DrawMatchStringInRect(this.text, this.TextRectangle, this.textPattern, new SolidBrush(ThemeManager.colors.quickTreeNodeFilteredTextForeground), backgroundBrush, true);
        if (isValidString(this.InstrumentTradingExchangeText)) {
            const brushColor = this.selected
                ? ThemeManager.colors.quickTreeNodeForegroundSelected
                : ThemeManager.colors.quickTreeNodeForegroundTradingExchange;
            ctx.DrawStringInRectNew(this.InstrumentTradingExchangeText, new SolidBrush(brushColor), exchangeTextRect, false);
        }
        if (isValidString(this.InstrumentDescriptionText)) {
            const descrY = this.TextRectangle.Y + font.getCorrectHeight();
            const brushColor = this.selected
                ? ThemeManager.colors.quickTreeNodeForegroundSelected
                : ThemeManager.colors.quickTreeNodeForegroundDescription;
            const descrFont = InstrumentLookupQuickTreeNode.DescriptionFontOgj;
            const descrRect = new Rectangle(this.TextRectangle.X, descrY, this.TextRectangle.Width, descrFont.getCorrectHeight());
            ctx.font = descrFont.toString();
            ctx.DrawStringInRectNew(this.InstrumentDescriptionText, new SolidBrush(brushColor), descrRect, true);
            ctx.DrawMatchStringInRect(this.InstrumentDescriptionText, descrRect, this.textPattern, new SolidBrush(ThemeManager.colors.quickTreeNodeFilteredTextForeground), backgroundBrush, true);
        }
        if (!isNullOrUndefined(this.FlagImg)) {
            const flagRect = new Rectangle(exchangeTextRect.Right() + gapBetweenExAndFlag, exchangeTextRect.Y, InstrumentLookupQuickTreeNode.InsFlagWidth, InstrumentLookupQuickTreeNode.InsFlagHeight);
            InstrumentLookupQuickTreeNode.DrawExchangeFlag(ctx, flagRect, this.FlagImg);
        }
    }
    setInstrumentTypeDisplay(instrumentType) {
        this.instrumentTypeText = SymbolLookupViewItemHelper.getInstrumentTypeShort(instrumentType);
        this.instrumentTypeColor = SymbolLookupViewItemHelper.getInstrumentTypeColor(instrumentType);
    }
    getTooltip(ctx) {
        const textWidth = ctx.GetTextWidth(this.text, InstrumentLookupQuickTreeNode.FontObj);
        if (textWidth > this.TextRectangle.Width) {
            return this.text;
        }
        return null;
    }
    get text() {
        return isValidString(this.InstrumentNameText) ? this.InstrumentNameText : this.nodeText;
    }
    drawCollapseIcon(ctx, hovered) {
        if (isNullOrUndefined(ctx) || isNullOrUndefined(this.CollapseRectangle)) {
            return;
        }
        let img;
        if (this.collapsed) {
            if (this.selected) {
                img = ThemeManager.NewImages.quickTreeExpandSelected.image;
            }
            else if (hovered) {
                img = ThemeManager.NewImages.quickTreeExpandHovered.image;
            }
            else {
                img = ThemeManager.NewImages.quickTreeExpandDefault.image;
            }
        }
        else {
            if (this.selected) {
                img = ThemeManager.NewImages.quickTreeCollapseSelected.image;
            }
            else if (hovered) {
                img = ThemeManager.NewImages.quickTreeCollapseHovered.image;
            }
            else {
                img = ThemeManager.NewImages.quickTreeCollapseDefault.image;
            }
        }
        if (isNullOrUndefined(img)) {
            return;
        }
        ctx.DrawCenteredImage(img, this.CollapseRectangle, img.width, img.height);
    }
    static DrawInstrumentTypeLogo(ctx, rect, instrumentTypeText, instrumentTypeColor) {
        if (!isValidString(instrumentTypeText)) {
            return;
        }
        const font = ThemeManager.Fonts.Font_10F_regular;
        const fontBrush = new SolidBrush(ThemeManager.colors.instrumentTypeLogoForeground);
        const typeLogoW = InstrumentLookupQuickTreeNode.InstrumentIconWidth;
        const typeLogoH = InstrumentLookupQuickTreeNode.InstrumentIconHeight;
        const xPadding = (rect.Width - typeLogoW) / 2;
        const yPadding = (rect.Height - typeLogoH) / 2;
        const typeLogoRect = new Rectangle(rect.X + xPadding, rect.Y + yPadding, typeLogoW, typeLogoH);
        const shadowFirst = new ShadowBrush(ThemeManager.colors.instrumentNodeElementShadowColorFirst, 2, 0, 1);
        const shadowSecond = new ShadowBrush(ThemeManager.colors.instrumentNodeElementShadowColorSecond, 2, 0, 0);
        ctx.drawRoundRectShadow(typeLogoRect, 2, shadowFirst, instrumentTypeColor);
        ctx.drawRoundRectShadow(typeLogoRect, 2, shadowSecond, instrumentTypeColor);
        ctx.DrawStringInRect(instrumentTypeText, font, fontBrush, typeLogoRect, false, 'center', 'middle');
    }
    static DrawExchangeFlag(ctx, rect, flagImg) {
        const shadowFirst = new ShadowBrush(ThemeManager.colors.instrumentNodeElementShadowColorFirst, 2, 0, 1);
        const shadowSecond = new ShadowBrush(ThemeManager.colors.instrumentNodeElementShadowColorSecond, 2, 0, 0);
        ctx.drawRoundRectShadow(rect, 2, shadowFirst);
        ctx.drawRoundRectShadow(rect, 2, shadowSecond);
        ctx.DrawCenteredImage(flagImg, rect);
    }
    static get Font() {
        const fontWeight = Resources.isHieroglyph() ? 'bold' : '';
        return ThemeManager.createFont(this._fontSize, fontWeight);
    }
    static get FontObj() {
        const fontWeight = Resources.isHieroglyph() ? 'bold' : '';
        return ThemeManager.createFontObj(this._fontSize, fontWeight);
    }
    static get DescriptionFont() {
        return ThemeManager.createFont(this._descriptionFontSize);
    }
    static get DescriptionFontOgj() {
        return ThemeManager.createFontObj(this._descriptionFontSize);
    }
}
InstrumentLookupQuickTreeNode.ROW_HEIGHT = 40;
InstrumentLookupQuickTreeNode.InstrumentIconWidth = 32;
InstrumentLookupQuickTreeNode.InstrumentIconHeight = 12;
InstrumentLookupQuickTreeNode.InsFlagWidth = 20;
InstrumentLookupQuickTreeNode.InsFlagHeight = 15;
//#region Static
InstrumentLookupQuickTreeNode._fontSize = 14;
InstrumentLookupQuickTreeNode._descriptionFontSize = 10;
